import {
  BuildingOfficeIcon,
  Chart,
  ClockIcon,
  FireIcon,
  Flex,
  Grid,
  PanelProps,
  RectangleStackIcon,
  SquaresPlusIcon,
  Text,
  UsersIcon,
} from 'components'
import dayjs from 'dayjs'
import { startCase } from 'lodash-es'
import { useEffect } from 'react'
import { AggregationData, DCFilter, DCTableName, LeadTemperature, leadTemperatures } from 'types'
import { useBreakpoint } from 'utils'
import { StatTile } from '..'
import { getFilterValueParams } from '../../utils'
import { useAggregations } from '../../utils/analyticsUtils'
import { useEvent } from '../../utils/eventUtils'
import EmptyState from '../EmptyState'
import LeadTempIcon from '../event/LeadTempIcon'

export default function EventAnalyticsOverviewSection({ onLoaded }: { onLoaded?: () => void }) {
  const isMobile = useBreakpoint() < 2
  const { event, filters = [], setFilters } = useEvent()
  const {
    apps,
    sessionsCount,
    companyCount,
    countsByDayAndDevice,
    moments,
    owners,
    schoolCount,
    stepDurations,
    temps,
  } = useOverviewAnalytics()
  const filtersApplied = filters.length > 0
  const tempCount = temps.data
    ?.filter(r => r.temp)
    .reduce((total, r) => total + ((r._count as number) || 0), 0)
  const avgDuration = stepDurations?.data?.reduce(
    (total, r) => total + ((r.duration_avg as number) || 0),
    0
  )
  const totalOwnerAssignments =
    owners.data?.reduce((total, row) => total + ((row?._count as number) || 0), 0) || 0

  useEffect(() => {
    if (countsByDayAndDevice.data && onLoaded) onLoaded()
  }, [countsByDayAndDevice])

  function handleSelect(field: string, value: string, table: DCTableName) {
    const { operator, value: filterValue } = getFilterValueParams(field, value, event!)
    const selectedFilters: DCFilter = {
      field,
      operator,
      value: filterValue,
      table: table,
    }
    setFilters([...filters, selectedFilters])
  }

  return sessionsCount === 0 ? (
    <EmptyState title="No data retrieved, change filters and try again" />
  ) : (
    <Flex direction="column">
      <Grid templateColumns={`repeat(auto-fill, minmax(270px, 1fr))`} gap={6}>
        <StatTile
          label={`${filters?.length ? '' : 'Total '}Sessions`}
          num={sessionsCount}
          Icon={UsersIcon}
        />
        {!!companyCount && (
          <StatTile label="Companies" num={companyCount} Icon={BuildingOfficeIcon} />
        )}
        {!!schoolCount && <StatTile label="Schools" num={schoolCount} Icon={BuildingOfficeIcon} />}

        {stepDurations?.data &&
          stepDurations.data.length < 3 &&
          stepDurations.data.map(({ title, duration_avg }) => {
            const duration = (duration_avg as number) || 0
            return (
              duration > 0 && (
                <StatTile
                  key={title}
                  label={`Average ${title} Duration`}
                  num={duration}
                  Icon={ClockIcon}
                />
              )
            )
          })}
      </Grid>

      <Flex gap={6} mt={6} flexWrap="wrap">
        <StatTile
          {...flexOverviewItemProps}
          flexBasis="450px"
          label="Lead Temperatures"
          num={tempCount}
          Icon={FireIcon}
          canCopy
        >
          <Flex flex={1} overflow="hidden" gap={5} px={5}>
            <Chart
              legend={{ customLegend: true }}
              {...temps}
              onSelect={
                isMobile
                  ? undefined
                  : ({ label }) => {
                      handleSelect('temp', label, 'leads')
                    }
              }
            />
            <Flex direction="column" gap={1}>
              {temps.data.map(({ temp, _count }) => (
                <Flex key={temp} direction="column">
                  <Flex gap={2} align="center">
                    <LeadTempIcon value={temp as LeadTemperature} />
                    <Text textStyle="bodySmall">{startCase((temp as string) || 'unset')}</Text>
                  </Flex>
                  <Text textStyle="h2" textAlign="center">
                    {_count}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </StatTile>

        {!!totalOwnerAssignments && (
          <StatTile
            {...flexOverviewItemProps}
            flexBasis={(owners.data?.length || 0) <= 5 ? '400px' : '700px'}
            label="Lead Owner Assignments"
            Icon={RectangleStackIcon}
            canCopy
          >
            <Chart
              {...owners}
              onSelect={
                isMobile
                  ? undefined
                  : ({ label }) => {
                      handleSelect('followUpGroup', label, 'leads')
                    }
              }
            />
          </StatTile>
        )}

        {(apps.data?.length ?? 0) > 1 || ((apps.data?.length ?? 0) === 1 && filtersApplied) ? (
          <StatTile
            {...flexOverviewItemProps}
            flexBasis="400px"
            label="Apps Used"
            Icon={RectangleStackIcon}
            canCopy
          >
            <Chart
              {...apps}
              onSelect={
                isMobile
                  ? undefined
                  : ({ label }) => {
                      handleSelect('appName', label, 'leadSessions')
                    }
              }
            />
          </StatTile>
        ) : undefined}

        {(moments.data?.length ?? 0) > 1 ||
        ((moments.data?.length ?? 0) === 1 && filtersApplied) ? (
          <StatTile
            {...flexOverviewItemProps}
            flexBasis="400px"
            label="Moments"
            Icon={SquaresPlusIcon}
            canCopy
          >
            <Chart
              {...moments}
              onSelect={
                isMobile
                  ? undefined
                  : ({ label }) => {
                      handleSelect('momentId', label, 'leadSessions')
                    }
              }
            />
          </StatTile>
        ) : undefined}

        <StatTile
          {...flexOverviewItemProps}
          flexBasis="750px"
          label="Average Session Duration"
          num={avgDuration}
          Icon={RectangleStackIcon}
          canCopy
        >
          <Chart
            type="bars"
            {...stepDurations}
            valueInfo={[{ label: 'Step Duration' }]}
            isStacked
          />
        </StatTile>
      </Flex>

      <StatTile
        mt={6}
        label="Sessions By Day"
        height="500px"
        alignItems="stretch"
        Icon={RectangleStackIcon}
        canCopy
      >
        <Chart
          type="combo"
          {...countsByDayAndDevice}
          categoryInfo={[{ formatter: v => dayjs(v).format('MMM D') }]}
          valueInfo={[{ type: 'line' }]}
          legend={{ show: true }}
        />
      </StatTile>
    </Flex>
  )
}

const flexOverviewItemProps: PanelProps = { alignItems: 'stretch', flexGrow: 1, height: '365px' }

function useOverviewAnalytics() {
  const { event, filters } = useEvent()
  const eventDCFilters = filters || []

  const { aggregations } = useAggregations([
    {
      filters: eventDCFilters,
      table: 'leadSessions',
      values: ['_count'],
    },
    {
      filters: [
        ...eventDCFilters,
        { field: 'company', operator: 'isNull', value: false, table: 'leads' },
      ],
      table: 'leads',
      values: ['_count'],
    },
    {
      filters: [
        ...eventDCFilters,
        { field: 'school', operator: 'isNull', value: false, table: 'leads' },
      ],
      table: 'leads',
      values: ['_count'],
    },
    {
      categories: ['title'],
      filters: [
        ...eventDCFilters,
        { field: 'groupTitle', operator: 'eq', value: '_default', table: 'sessionMetrics' },
      ],
      table: 'sessionMetrics',
      values: ['duration_avg'],
    },
    {
      categories: ['temp'],
      filters: eventDCFilters,
      table: 'leads',
      values: ['_count'],
    },
    {
      categories: ['appName'],
      filters: eventDCFilters,
      table: 'leadSessions',
      values: ['_count'],
    },
    {
      categories: ['momentId'],
      filters: eventDCFilters,
      table: 'leadSessions',
      sort: { _count: 'DESC' },
      values: ['_count'],
    },
    {
      categories: ['createdDay', 'deviceName'],
      filters: eventDCFilters,
      table: 'leadSessions',
      sort: { createdDay: 'ASC' },
      values: ['_count'],
    },
    {
      categories: ['followUpGroup'],
      filters: [
        ...eventDCFilters,
        { field: 'followUpGroup', operator: 'isNull', value: false, table: 'leads' },
      ],
      table: 'leads',
      values: ['_count'],
      sort: { _count: 'DESC' },
    },
  ])

  const [
    sessionsCount,
    companyCount,
    schoolCount,
    stepDurations,
    temps,
    apps,
    moments,
    countsByDayAndDevice,
    owners,
  ] = aggregations || {}

  const sortedDurations = stepDurations?.data?.sort((a, b) => {
    if (!a.title || !b.title) return 0
    return durationsOrder.indexOf(a.title as string) - durationsOrder.indexOf(b.title as string)
  })
  const sortedTemps = [...leadTemperatures, null]
    .map(label => {
      return temps?.data?.find(r => r.temp === label)
    })
    .filter(Boolean) as AggregationData
  const momentData =
    moments?.data?.map(r => {
      const { title, type } = event?.moments.find(m => m.id === r.momentId) || {}
      return { ...r, momentTitle: `${title} (${renderMomentType(type)})` }
    }) || []

  return {
    apps,
    sessionsCount: sessionsCount?.data?.[0]?._count as number,
    companyCount: companyCount?.data?.[0]?.company_count as number,
    countsByDayAndDevice,
    moments: { ...moments, data: momentData, categories: ['momentTitle'] },
    schoolCount: schoolCount?.data?.[0]?.school_count as number,
    stepDurations: { ...stepDurations, data: sortedDurations },
    temps: { ...temps, data: sortedTemps },
    owners,
  }
}

function renderMomentType(type?: string) {
  switch (type) {
    case 'public':
      return 'Self Registration'
    default:
      return startCase(type)
  }
}

const durationsOrder = ['Registration', 'Traits', 'Results']
